import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation,
  faCircleCheck,
  faClockRotateLeft,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { fmtUtcFull, fmtDate } from './format';

// What the analytics pages used to show as a banner, moved here unchanged in
// meaning. Two separate failure modes are on show:
//
//   1. isSignedOff === false -- the warehouse is mid-soak, so the figures are for
//      verification and must not be handed to store operators as authoritative.
//   2. hoursSinceLastRun > 30 -- the nightly load did not run, and without this
//      a dashboard cheerfully shows yesterday's numbers as though they were today's.
//
// A failed or aborted last run is its own state too: the API's statusMessage
// ranks it above everything else, so the headline must not contradict it with a
// green "signed off".
//
// Since the legacy cutover (isCutOver) the soak is history: the gate writes no
// verdicts, and each load is judged by its check against the source instead. A
// failed check outranks "running" -- a check that threw leaves the run reading
// Running until it is auto-aborted the next day.
//
// Status colour never carries the meaning alone: each state ships an icon and a
// sentence.
function StatusCard({ status }) {
  const { t } = useTranslation();
  if (!status) return null;

  const stale = (status.hoursSinceLastRun ?? 0) > 30;
  const failed = status.lastRunStatus === 'Failed' || status.lastRunStatus === 'Aborted';
  const running = status.isRunInProgress;
  const signedOff = status.isSignedOff;
  const cutOver = status.isCutOver;
  const checkFailed = cutOver && status.lastSourceCheckStatus === 'Failed';

  let tone;
  let headline;
  if (stale || failed || checkFailed) {
    tone = { bg: 'bg-rose-50', border: 'border-danger/30', text: 'text-danger', icon: faTriangleExclamation };
    if (failed) headline = t('dw_last_load_failed');
    else if (checkFailed) headline = t('dw_source_check_failed');
    else headline = t('analytics_data_stale');
  } else if (running) {
    tone = { bg: 'bg-teal-50', border: 'border-info/30', text: 'text-info', icon: faSpinner };
    headline = t('dw_load_running');
  } else if (signedOff) {
    tone = { bg: 'bg-emerald-50', border: 'border-success/30', text: 'text-success', icon: faCircleCheck };
    headline = cutOver ? t('dw_warehouse_live') : t('analytics_signed_off');
  } else {
    tone = { bg: 'bg-amber-50', border: 'border-warning/40', text: 'text-warning', icon: faTriangleExclamation };
    headline = t('analytics_validation_in_progress');
  }

  return (
    <div className={`${tone.bg} ${tone.border} rounded-card border px-4 py-3`}>
      <div className="flex items-start gap-3">
        <FontAwesomeIcon icon={tone.icon} className={`${tone.text} mt-0.5`} spin={tone.icon === faSpinner} />
        <div className="min-w-0 flex-1">
          <div className={`${tone.text} text-sm font-semibold`}>{headline}</div>
          <div className="mt-0.5 text-xs leading-relaxed text-ink">{status.statusMessage}</div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted tabular-nums">
            <span>
              <FontAwesomeIcon icon={faClockRotateLeft} className="mr-1.5" />
              {t('analytics_last_load')}: {fmtUtcFull(status.lastRunCompletedAtUtc)} UTC
              {status.hoursSinceLastRun != null && ` (${t('dw_hours_since_start', { hours: status.hoursSinceLastRun })})`}
            </span>
            <span>
              {t('analytics_latest_business_day')}: {fmtDate(status.latestSalesDate)}
            </span>
            {cutOver ? (
              <span>
                {t('dw_source_check')}: {status.lastSourceCheckStatus ?? t('dw_source_check_not_run')}
              </span>
            ) : (
              <span>
                {t('analytics_soak_progress')}: {status.consecutivePassingNights}/{status.nightsRequired}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatusCard;
